/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Card, Collapse } from '@mui/material';
import { type ReactNode, useContext, useState, useEffect } from 'react';

import { SearchContext } from '../../../contexts/SearchContext';
import { CapabilityItem } from './CapabilityItem/CapabilityItem';
import { FeatureItem } from './FeatureItem/FeatureItem';
import { type PluginSelectionBasicViewProps } from './types';
import { kebabCase } from 'lodash';
import { GTMContext } from '../../../contexts/GTMContext';
import { GTM_EVENT_GROUP_COLLAPSE, GTM_EVENT_GROUP_UNCOLLAPSE } from '../../../constants/GTMEventNames';

export function PluginSelectionBasicView( {
	filteredCapabilities,
	onPluginSelectionChange,
	onGroupToggle,
	toolbarOptions
}: PluginSelectionBasicViewProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );
	const searchQuery = useContext( SearchContext );
	const [ expandedCapabilities, setExpandedCapabilities ] = useState<Set<string>>( new Set() );

	useEffect( () => {
		if ( searchQuery ) {
			const foundCapabilities = filteredCapabilities
				.filter( capability => !capability.isFiltered )
				.map( capability => capability.name );

			setExpandedCapabilities( new Set( foundCapabilities ) );
		}
	}, [ searchQuery, filteredCapabilities ] );

	function handleExpandCapability( capabilityName: string, isExpanded: boolean ) {
		setExpandedCapabilities( previousValue => {
			if ( isExpanded ) {
				previousValue.delete( capabilityName );

				triggerGTMEvent( {
					event: GTM_EVENT_GROUP_COLLAPSE,
					group: capabilityName
				} );
			} else {
				previousValue.add( capabilityName );

				triggerGTMEvent( {
					event: GTM_EVENT_GROUP_UNCOLLAPSE,
					group: capabilityName
				} );
			}

			return new Set( previousValue );
		} );
	}

	return (
		filteredCapabilities
			.filter( capability => !capability.isFiltered )
			.map( capability => {
				const selectedFeaturesCount = capability.features.filter( feature => {
					return feature.isSelected || feature.isSelectedIndeterminate;
				} ).length;
				const totalFeaturesCount = capability.features.filter( f => !f.isDisabled ).length;
				const expandText = `(${ selectedFeaturesCount }/${ totalFeaturesCount }) Features`;
				const isExpanded = expandedCapabilities.has( capability.name );

				return (
					<Card
						key={ capability.name }
						sx={ { overflow: 'initial' } }
						aria-expanded={ isExpanded }
						data-testid={ `card-${ kebabCase( capability.name ) }` }
					>
						<CapabilityItem
							capability={ capability }
							expandText={ expandText }
							isExpanded={ isExpanded }
							onPluginSelectionChange={ onPluginSelectionChange }
							onGroupToggle={ onGroupToggle }
							onCardClick={ () => handleExpandCapability( capability.name, isExpanded ) }
						/>
						<Collapse in={ isExpanded }>
							{ isExpanded && capability.features
								.filter( feature => !feature.isFiltered )
								.map( ( feature, index, features ) => {
									const isLast = index === features.length - 1;

									return (
										<FeatureItem
											key={ feature.name }
											feature={ feature }
											isLast={ isLast }
											onPluginSelectionChange={ onPluginSelectionChange }
											isSingleFeatureChoice={ capability.isSingleFeatureChoice }
											toolbarOptions={ toolbarOptions }
										/>
									);
								} ) }
						</Collapse>
					</Card>
				);
			} )
	);
}
